import { useState, useRef, useEffect } from 'react';
import { orderService, CreateOrderData } from '../services/orderService';
import { Order } from '../types';
import { useAppData } from '../context/AppDataContext';
import { defaultOrderFormData, orderToFormData } from '../utils/orderFormUtils';
import { getTodayISO } from '../utils/dateUtils';

export interface UseOrderFormModalOptions {
  onSaved?: () => void | Promise<void>;
}

export function useOrderFormModal(options: UseOrderFormModalOptions = {}) {
  const { users, brands, suppliers, customers, refreshCustomers } = useAppData();
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [formData, setFormData] = useState<CreateOrderData>(defaultOrderFormData());
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [modalError, setModalError] = useState('');
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setCustomerDropdownOpen(false);
      }
    };
    if (customerDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [customerDropdownOpen]);

  const closeModal = () => {
    setShowModal(false);
    setEditingOrder(null);
    setCustomerDropdownOpen(false);
    setCustomerSearch('');
    setModalError('');
  };

  const openForCreate = (prefill?: Partial<CreateOrderData>) => {
    setEditingOrder(null);
    setCustomerDropdownOpen(false);
    setCustomerSearch('');
    setFormData({
      ...defaultOrderFormData(),
      order_date: getTodayISO(),
      ...prefill,
    });
    setModalError('');
    setShowModal(true);
  };

  const openForEdit = (order: Order) => {
    setEditingOrder(order);
    setCustomerDropdownOpen(false);
    setCustomerSearch('');
    setFormData(orderToFormData(order, customers));
    setModalError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    try {
      if (editingOrder) {
        await orderService.update(editingOrder.id, formData);
      } else {
        await orderService.create(formData);
      }
      closeModal();
      setFormData(defaultOrderFormData());
      await Promise.all([options.onSaved?.(), refreshCustomers()]);
    } catch (err: any) {
      setModalError(err.response?.data?.message || 'Failed to save order');
    }
  };

  return {
    showModal,
    editingOrder,
    formData,
    setFormData,
    customerSearch,
    setCustomerSearch,
    customerDropdownOpen,
    setCustomerDropdownOpen,
    customerDropdownRef,
    modalError,
    setModalError,
    users,
    brands,
    suppliers,
    customers,
    openForCreate,
    openForEdit,
    closeModal,
    handleSubmit,
  };
}
